import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { defaultTreinamentoForm, TreinamentoForm, TreinamentoSchema } from "../../../schemas/Treinamentos_lista/treinamento";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { Treinamento } from "../../../../domain/models/treinamento";
import { FormTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_time_masked_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

/**
 * CadastroTreinamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedTreinamento: Treinamento | null,
  onSubmit: (form: TreinamentoForm) => void,
  onOpenChangeHistory: () => void,
  onClose: () => void,
};

export const CadastroTreinamentoModal = ({
  open,
  saving,
  selectedTreinamento,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<TreinamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultTreinamentoForm,
    resolver: zodResolver(TreinamentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedTreinamento) {
        hookForm.setValue('ativo', selectedTreinamento.ativo ? 1 : 0);
        hookForm.setValue('nome', selectedTreinamento.nome ?? '');
        hookForm.setValue('versao', selectedTreinamento.versao ?? '');
        hookForm.setValue('cargaHoraria', selectedTreinamento.cargaHoraria ?? 0);
        hookForm.setValue('conteudoProgramatico', selectedTreinamento.conteudoProgramatico ?? '');
      } else {
        hookForm.reset();
      }
    }
  }, [
    open,
    selectedTreinamento,
    hookForm,
  ]);

  const handleSubmit = useCallback((form: TreinamentoForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "85rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Treinamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <FormSwitchInput
                name='ativo'
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='nome'
                label='Treinamento'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='versao'
                label='Versão'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm} />

              <FormTimeMaskedInput
                name='cargaHoraria'
                label='Carga Horária'
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='conteudoProgramatico'
                label='Conteúdo Programático'
                readOnly={saving}
                multiline={true}
                rows={10}
                reactHookForm={hookForm}
              />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedTreinamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}